import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import type {DraftOrderDetail} from '../types/myaccount';
import api from '../services/api';
import {accountActionIconGroupStyle, accountActionIconStyle} from './accountActionIconStyles';
import DataTable from './DataTable';
import MyAccountCard from './MyAccountCard';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar, PageShellLoading, TableSkeleton } from './MyAccountLoadingStates';

const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return dateString;
    }

    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};

interface ViewDraftOrderProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const ViewDraftOrder: React.FC<ViewDraftOrderProps> = ({ wekaPagePermissions = {} }) => {
    const navigate = useNavigate();
    const {draftId} = useParams<{ draftId: string }>();
    const [draftDetail, setDraftDetail] = useState<DraftOrderDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeRequest, setActiveRequest] = useState<string | null>(null);
    const [renameDraftName, setRenameDraftName] = useState('');
    const [itemQuantities, setItemQuantities] = useState<Record<string, string>>({});
    const canRenameDraft = wekaPagePermissions.rename_draft !== false;
    const canCheckoutDraft = wekaPagePermissions.checkout_draft !== false;
    const canDeleteDraft = wekaPagePermissions.delete_draft !== false;
    const canEditDraftItems = wekaPagePermissions.edit_draft_items !== false;

    const resolvedDraftId = Number(draftId || 0);

    const syncItemQuantities = (detail: DraftOrderDetail) => {
        setItemQuantities(() => {
            const next: Record<string, string> = {};
            detail.items.forEach((item) => {
                next[item.item_key] = String(item.quantity ?? '');
            });
            return next;
        });
    };

    const fetchDraftDetail = async () => {
        if (!Number.isFinite(resolvedDraftId) || resolvedDraftId <= 0) {
            setLoading(false);
            setError('Draft order not found.');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const data = await api.getDraftOrder(resolvedDraftId);
            setDraftDetail(data);
            setRenameDraftName(data.name || '');
            syncItemQuantities(data);
        } catch (err) {
            console.error('Error fetching draft order detail:', err);
            setError(err instanceof Error ? err.message : 'Failed to load draft order.');
            setDraftDetail(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void fetchDraftDetail();
    }, [resolvedDraftId]);

    const handleRenameDraft = async () => {
        if (!canRenameDraft) {
            toast.error('Renaming draft orders is disabled for this page.');
            return;
        }

        if (!draftDetail) {
            return;
        }

        const requestKey = `rename:${draftDetail.id}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.updateDraftOrder(draftDetail.id, {
                draft_name: renameDraftName.trim(),
            });
            setDraftDetail(response.draft);
            setRenameDraftName(response.draft.name || '');
            syncItemQuantities(response.draft);
            toast.success(response.message);
        } catch (err) {
            console.error('Error renaming draft order:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to rename draft order.');
        } finally {
            setActiveRequest(null);
        }
    };

    const handleCheckoutDraft = async () => {
        if (!canCheckoutDraft) {
            toast.error('Checking out draft orders is disabled for this page.');
            return;
        }

        if (!draftDetail) {
            return;
        }

        const requestKey = `convert:${draftDetail.id}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.convertDraftOrder(draftDetail.id);
            toast.success(response.message);
            window.location.assign(response.checkout_url);
        } catch (err) {
            console.error('Error preparing draft for checkout:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to prepare draft for checkout.');
        } finally {
            setActiveRequest(null);
        }
    };

    const handleDeleteDraft = async () => {
        if (!canDeleteDraft) {
            toast.error('Deleting draft orders is disabled for this page.');
            return;
        }

        if (!draftDetail) {
            return;
        }

        if (!window.confirm('Delete this draft order?')) {
            return;
        }

        const requestKey = `delete:${draftDetail.id}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.deleteDraftOrder(draftDetail.id);
            toast.success(response.message);
            navigate('/draft-orders');
        } catch (err) {
            console.error('Error deleting draft order:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to delete draft order.');
        } finally {
            setActiveRequest(null);
        }
    };

    const handleUpdateItemQuantity = async (itemKey: string) => {
        if (!canEditDraftItems) {
            toast.error('Editing draft items is disabled for this page.');
            return;
        }

        if (!draftDetail) {
            return;
        }

        const rawValue = String(itemQuantities[itemKey] ?? '').trim();
        const parsedQuantity = Number(rawValue);
        if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
            toast.error('Enter a valid quantity greater than zero.');
            return;
        }

        const requestKey = `item-update:${itemKey}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.updateDraftOrderItem(draftDetail.id, itemKey, {
                quantity: parsedQuantity,
            });
            setDraftDetail(response.draft);
            setRenameDraftName(response.draft.name || '');
            syncItemQuantities(response.draft);
            toast.success(response.message);
        } catch (err) {
            console.error('Error updating draft item:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to update draft item.');
        } finally {
            setActiveRequest(null);
        }
    };

    const handleRemoveItem = async (itemKey: string) => {
        if (!canEditDraftItems) {
            toast.error('Editing draft items is disabled for this page.');
            return;
        }

        if (!draftDetail) {
            return;
        }

        if (!window.confirm('Remove this item from the draft?')) {
            return;
        }

        const requestKey = `item-remove:${itemKey}`;
        try {
            setActiveRequest(requestKey);
            const response = await api.removeDraftOrderItem(draftDetail.id, itemKey);
            setDraftDetail(response.draft);
            setRenameDraftName(response.draft.name || '');
            syncItemQuantities(response.draft);
            toast.success(response.message);
        } catch (err) {
            console.error('Error removing draft item:', err);
            toast.error(err instanceof Error ? err.message : 'Failed to remove draft item.');
        } finally {
            setActiveRequest(null);
        }
    };

    if (loading) {
        return (
            <PageShellLoading
                showAction={true}
                titleWidth="28%"
                subtitleWidth="56%"
                actionWidth="220px"
            >
                <MyAccountCard
                    variant="page"
                    slug="view-draft-order-loading"
                    className="weka-my-account-page__section-card"
                    innerClassName="d-flex flex-column gap-4"
                    wrapContent={false}
                >
                        <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
                            <LoadingBar height="38px" width="100%" style={{ borderRadius: '8px', flex: 1 }} />
                            <LoadingBar height="38px" width="120px" style={{ borderRadius: '8px' }} />
                        </div>
                        <TableSkeleton columns={5} rows={6} />
                </MyAccountCard>
            </PageShellLoading>
        );
    }

    if (error || !draftDetail) {
        return (
            <MyAccountPageShell
                title="Draft Order"
                subtitle="Review and update the products saved in this draft."
            >
                <div className="woocommerce-error mb-3" role="alert">
                    {error || 'Draft order not found.'}
                </div>
                <Link to="/draft-orders" className="button">
                    Back to Draft Orders
                </Link>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell
            title={draftDetail.name || 'Draft Order'}
            subtitle={`Created ${formatDate(draftDetail.date_created)} • ${draftDetail.items_count} items • $${draftDetail.total}`}
            actions={(
                <>
                    <Link to="/draft-orders" className="button">
                        Back to Draft Orders
                    </Link>
                    <button
                        type="button"
                        className="button"
                        onClick={() => { void handleCheckoutDraft(); }}
                        disabled={activeRequest !== null || !canCheckoutDraft}
                    >
                        {activeRequest === `convert:${draftDetail.id}` ? 'Redirecting...' : 'Checkout'}
                    </button>
                    <button
                        type="button"
                        className="button"
                        onClick={() => { void handleDeleteDraft(); }}
                        disabled={activeRequest !== null || !canDeleteDraft}
                    >
                        {activeRequest === `delete:${draftDetail.id}` ? 'Deleting...' : 'Delete'}
                    </button>
                </>
            )}
        >
            {(!canRenameDraft || !canCheckoutDraft || !canDeleteDraft || !canEditDraftItems) && (
                <div className="alert alert-info" role="status">
                    {[
                        !canRenameDraft ? 'Renaming draft orders is disabled.' : '',
                        !canCheckoutDraft ? 'Draft checkout is disabled.' : '',
                        !canDeleteDraft ? 'Deleting draft orders is disabled.' : '',
                        !canEditDraftItems ? 'Editing draft items is disabled.' : '',
                    ].filter(Boolean).join(' ')}
                </div>
            )}

            <MyAccountCard
                variant="page"
                slug="view-draft-order-items"
                className="weka-my-account-page__section-card"
                innerClassName="d-flex flex-column gap-4"
                wrapContent={false}
            >
                    <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
                        <input
                            type="text"
                            className="form-control"
                            value={renameDraftName}
                            onChange={(event) => setRenameDraftName(event.currentTarget.value)}
                            placeholder="Draft name"
                            disabled={activeRequest !== null || !canRenameDraft}
                        />
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => { void handleRenameDraft(); }}
                            disabled={activeRequest !== null || !canRenameDraft}
                        >
                            {activeRequest === `rename:${draftDetail.id}` ? 'Saving...' : 'Save'}
                        </button>
                    </div>

                    {draftDetail.items.length === 0 ? (
                        <div className="alert alert-light border mb-0">
                            This draft is empty.
                        </div>
                    ) : (
                        <DataTable
                            rows={draftDetail.items}
                            rowKey={(item) => item.item_key}
                            wrapperClassName=""
                            responsiveMode="stacked-cards"
                            tableClassName="table table-hover align-middle mb-0"
                            tableStyle={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                            rowStyle={{
                                borderBottom: '1px solid var(--weka-subtle-border-color)',
                            }}
                            columns={[
                                {
                                    id: 'item',
                                    header: 'ITEM',
                                    mobileLabel: 'Item',
                                    mobileDisplay: 'title',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                    cellStyle: { padding: '12px' },
                                    render: (item) => (
                                        <>
                                            <div className="fw-semibold">{item.name}</div>
                                            {item.sku && item.sku.trim() !== '' && (
                                                <div className="text-muted small">SKU: {item.sku}</div>
                                            )}
                                            {Array.isArray(item.option_summary) && item.option_summary.length > 0 && (
                                                <div className="text-muted small">
                                                    {item.option_summary.join(' · ')}
                                                </div>
                                            )}
                                        </>
                                    ),
                                },
                                {
                                    id: 'quantity',
                                    header: 'QUANTITY',
                                    mobileLabel: 'Quantity',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                    cellStyle: { padding: '12px', minWidth: '190px' },
                                    render: (item) => (
                                        <input
                                            type="number"
                                            className="form-control form-control-sm"
                                            step="any"
                                            min="0.000001"
                                            value={itemQuantities[item.item_key] ?? String(item.quantity ?? '')}
                                            onChange={(event) => {
                                                const nextValue = event.currentTarget.value;
                                                setItemQuantities((current) => ({
                                                    ...current,
                                                    [item.item_key]: nextValue,
                                                }));
                                            }}
                                            disabled={activeRequest !== null || !canEditDraftItems}
                                        />
                                    ),
                                },
                                {
                                    id: 'unit_price',
                                    header: 'UNIT PRICE',
                                    mobileLabel: 'Unit price',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                    cellStyle: { padding: '12px' },
                                    render: (item) => `$${item.unit_price}`,
                                },
                                {
                                    id: 'total',
                                    header: 'TOTAL',
                                    mobileLabel: 'Total',
                                    mobileDisplay: 'field',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                    cellStyle: { padding: '12px' },
                                    render: (item) => `$${item.line_total}`,
                                },
                                {
                                    id: 'actions',
                                    header: 'ACTIONS',
                                    mobileDisplay: 'actions',
                                    headerStyle: { padding: '12px', textAlign: 'left', fontWeight: '600', color: '#495057' },
                                    cellStyle: { padding: '12px' },
                                    render: (item) => (
                                        <div style={accountActionIconGroupStyle}>
                                            <button
                                                type="button"
                                                onClick={() => { void handleUpdateItemQuantity(item.item_key); }}
                                                disabled={activeRequest !== null || !canEditDraftItems}
                                                title="Update quantity"
                                                aria-label="Update quantity"
                                                style={accountActionIconStyle}
                                            >
                                                <i
                                                    className={activeRequest === `item-update:${item.item_key}` ? 'fas fa-spinner fa-spin' : 'fas fa-save'}
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => { void handleRemoveItem(item.item_key); }}
                                                disabled={activeRequest !== null || !canEditDraftItems}
                                                title="Remove item"
                                                aria-label="Remove item"
                                                style={accountActionIconStyle}
                                            >
                                                <i
                                                    className={activeRequest === `item-remove:${item.item_key}` ? 'fas fa-spinner fa-spin' : 'fas fa-trash'}
                                                    aria-hidden="true"
                                                ></i>
                                            </button>
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    )}
            </MyAccountCard>
        </MyAccountPageShell>
    );
};

export default ViewDraftOrder;
